import React, { useState } from 'react';
import {
  Scale, ChevronLeft, Search, X, Plus, School, MapPin,
  Trophy, Users, CheckCircle, BookOpen, Building2
} from 'lucide-react';

// Import school data
import { schoolRankings2024, publicSchools, independentSchools } from '../data/education-data';

const BackButton = ({ goBack, label = "Back" }) => (
  <button onClick={goBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 font-medium">
    <ChevronLeft className="w-5 h-5" />{label}
  </button>
);

// Sector for a ranked school, looked up by name. The ranking file carries no
// sector of its own, and every ranked school used to be typed "Ranked" — so the
// Type row read "Ranked / Ranked / Ranked" whenever you compared top schools,
// which is the one case where the row had nothing to say.
//
// The two datasets do not agree on names, so an exact match fails for 9 of the
// 28 ranked schools and would label them "Not listed" — worse than the bug it
// replaced. Two steps fix it:
//
//   1. Normalise away punctuation, "The" and the word "School".
//   2. Alias the six that genuinely differ.
//
// Note what is NOT normalised away: "Boys", "Girls", "College". Dropping them
// collapses "Wolmer's Boys School" and "Wolmer's High School for Girls" onto
// the same key — two different schools, and the alias table would then be
// resolving a collision rather than a spelling.
const normaliseName = (n) =>
  n
    .toLowerCase()
    .replace(/[.,'’"]/g, '')
    .replace(/\b(school|the)\b/g, ' ')
    .replace(/\bsaint\b/g, 'st')
    .replace(/\s+/g, ' ')
    .trim();

// ranking-file name -> directory name. Verified one by one against
// schools-public.json / schools-independent.json.
const NAME_ALIASES = {
  'St Andrew High School for Girls': 'St. Andrew High',
  'Montego Bay High School for Girls': 'Montego Bay High',
  "Manning's School": "Manning's High",
  "St Hilda's High": "St. Hilda's Diocesan High",
  "Wolmer's Girls School": "Wolmer's High School for Girls",
  'Convent of Mercy Alpha': 'Convent of Mercy Academy "Alpha"',
};

const SECTOR_BY_NAME = (() => {
  const map = new Map();
  publicSchools.forEach(s => map.set(normaliseName(s.school_name), 'Public'));
  independentSchools.forEach(s => {
    const key = normaliseName(s.school_name);
    if (!map.has(key)) map.set(key, 'Independent');
  });
  return map;
})();

const sectorFor = (name) =>
  SECTOR_BY_NAME.get(normaliseName(NAME_ALIASES[name] || name)) || 'Not listed';

// Combine schools for selection
const allSchoolsForComparison = [
  ...schoolRankings2024.map(s => ({
    id: s.rank,
    name: s.school_name,
    parish: s.parish,
    gender: s.gender,
    score: s.score,
    rank: s.rank,
    type: sectorFor(s.school_name)
  })),
  ...publicSchools.filter(s => !schoolRankings2024.find(r => r.school_name === s.school_name)).map((s, i) => ({
    id: `pub-${i}`,
    name: s.school_name,
    parish: s.parish,
    gender: s.gender || 'Co-educational',
    score: null,
    rank: null,
    type: 'Public'
  })),
  ...independentSchools.filter(s => !schoolRankings2024.find(r => r.school_name === s.school_name)).map((s, i) => ({
    id: `ind-${i}`,
    name: s.school_name,
    parish: s.parish,
    gender: s.gender || 'Co-educational',
    score: null,
    rank: null,
    type: 'Independent'
  }))
];

export const CompareSchoolsPage = ({ setPage, goBack }) => {
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const filteredSchools = allSchoolsForComparison.filter(school =>
    school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    school.parish.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 20);

  const addSchool = (school) => {
    if (selectedSchools.length < 3 && !selectedSchools.find(s => s.id === school.id)) {
      setSelectedSchools([...selectedSchools, school]);
      setShowSearch(false);
      setSearchTerm('');
    }
  };

  const removeSchool = (schoolId) => {
    setSelectedSchools(selectedSchools.filter(s => s.id !== schoolId));
  };

  // Every row is one <tr>, so the columns line up by construction.
  //
  // This used to be a stack of divs with `grid-cols-${n + 1}` built at render
  // time. Tailwind compiles the classes it can see in the source, and that
  // string never appears there — so no grid-cols rule was ever emitted and the
  // "side-by-side" comparison collapsed to a single centred column: every
  // school's parish stacked under every other school's parish, with no way to
  // tell which value belonged to which school. A table also gives the browser
  // the header-to-cell association a screen reader needs, which the div grid
  // could not express at all.
  const ComparisonRow = ({ label, icon: Icon, values, highlight = false, best = null }) => (
    <tr className={highlight ? 'bg-brand-50' : undefined}>
      {/* Sticky, so the measure stays on screen while the school columns
          scroll under it — otherwise on a phone you scroll right to read the
          third school and lose track of which row you are on. Needs its own
          opaque background: a sticky cell scrolls over its neighbours. */}
      <th
        scope="row"
        className={`sticky left-0 z-10 py-3 pr-4 text-left align-middle font-medium text-sm text-ink-muted whitespace-nowrap border-b border-hairline ${
          highlight ? 'bg-brand-50' : 'bg-canvas-raised'
        }`}
      >
        <span className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />}
          {label}
        </span>
      </th>
      {values.map((value, i) => (
        <td
          key={i}
          className={`py-3 px-3 text-center align-middle border-b border-hairline ${
            best === i ? 'font-extrabold text-brand-800' : 'font-medium text-ink'
          }`}
        >
          {value}
          {best === i && <span className="sr-only"> (best of the selected schools)</span>}
        </td>
      ))}
    </tr>
  );

  // Index of the strongest value, so the eye lands on it without reading every
  // cell. Null when nothing is comparable — an unranked school has no score,
  // and two schools tying is not a winner.
  const bestIndex = (nums) => {
    const vals = nums.map(n => (typeof n === 'number' ? n : null));
    const present = vals.filter(v => v !== null);
    if (present.length < 2) return null;
    const top = Math.max(...present);
    return vals.filter(v => v === top).length > 1 ? null : vals.indexOf(top);
  };

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      {/* Header */}
      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-xl sm:rounded-2xl p-4 sm:p-8 text-white mb-6 sm:mb-8">
        <Scale className="w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4" />
        <h1 className="text-2xl sm:text-3xl font-black mb-2">Compare Schools</h1>
        <p className="text-white/90 text-sm sm:text-base">Select up to 3 schools to compare side by side</p>
      </div>

      {/* School Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 className="font-bold text-gray-800 mb-4">Selected Schools ({selectedSchools.length}/3)</h2>

        <div className="grid md:grid-cols-3 gap-4 mb-4">
          {selectedSchools.map((school, index) => (
            <div key={school.id} className="relative bg-brand-50/60 rounded-xl p-4 ring-1 ring-brand-200">
              <button
                onClick={() => removeSchool(school.id)}
                aria-label={`Remove ${school.name} from the comparison`}
                className="absolute -top-2 -right-2 w-7 h-7 bg-canvas-raised text-ink-muted rounded-full ring-1 ring-hairline shadow-sm flex items-center justify-center transition-colors hover:bg-red-50 hover:text-red-600 hover:ring-red-200"
              >
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
              <div className="text-center">
                <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <School className="w-6 h-6 text-brand-700" aria-hidden="true" />
                </div>
                <h3 className="font-bold text-gray-800 text-sm leading-tight">{school.name}</h3>
                <p className="text-xs text-gray-500 mt-1">{school.parish}</p>
              </div>
            </div>
          ))}

          {selectedSchools.length < 3 && (
            <button
              onClick={() => setShowSearch(true)}
              className="bg-canvas rounded-xl p-4 border-2 border-dashed border-hairline hover:border-brand-400 hover:bg-brand-50 transition-colors flex flex-col items-center justify-center min-h-[120px]"
            >
              <Plus className="w-8 h-8 text-gray-400 mb-2" />
              <span className="text-sm text-gray-500">Add School</span>
            </button>
          )}
        </div>

        {/* Search Modal */}
        {showSearch && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-gray-800">Search Schools</h3>
                  <button onClick={() => setShowSearch(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="relative">
                  <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by school name or parish..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
                    autoFocus
                  />
                </div>
              </div>
              <div className="overflow-y-auto max-h-[50vh]">
                {filteredSchools.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No schools found matching "{searchTerm}"
                  </div>
                ) : (
                  filteredSchools.map(school => (
                    <button
                      key={school.id}
                      onClick={() => addSchool(school)}
                      disabled={selectedSchools.find(s => s.id === school.id)}
                      className={`w-full p-4 text-left border-b hover:bg-gray-50 flex items-center gap-3 ${
                        selectedSchools.find(s => s.id === school.id) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <School className="w-5 h-5 text-brand-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-800 truncate">{school.name}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-2">
                          <MapPin className="w-3 h-3" /> {school.parish}
                          {school.rank && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                              Rank #{school.rank}
                            </span>
                          )}
                        </div>
                      </div>
                      {selectedSchools.find(s => s.id === school.id) ? (
                        <CheckCircle className="w-5 h-5 text-brand-500" />
                      ) : (
                        <Plus className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Comparison Table */}
      {selectedSchools.length >= 2 ? (
        <div className="bg-canvas-raised rounded-xl shadow-sm border border-hairline p-4 sm:p-6">
          <h2 className="font-bold text-ink mb-4">Comparison</h2>

          {/* Two or three long school names will not fit a phone. Rather than
              truncate the names or shrink the type, the table scrolls inside
              its own container — the page itself never scrolls sideways. */}
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
            <table className="w-full min-w-[520px] border-collapse text-[15px]">
              <caption className="sr-only">
                {selectedSchools.map(s => s.name).join(', ')} compared by parish, gender, sector,
                academic rank and performance score
              </caption>
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="sticky left-0 z-10 w-32 sm:w-40 pb-3 bg-canvas-raised border-b-2 border-hairline"
                  >
                    <span className="sr-only">Measure</span>
                  </th>
                  {/* Parish is a row of its own directly below; repeating it
                      under each name just made the header twice as tall. */}
                  {selectedSchools.map((school, i) => (
                    <th
                      key={i}
                      scope="col"
                      className="px-3 pb-3 text-center align-bottom font-bold text-ink text-sm leading-tight border-b-2 border-hairline"
                    >
                      {school.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <ComparisonRow
                  label="Parish"
                  icon={MapPin}
                  values={selectedSchools.map(s => s.parish)}
                />
                <ComparisonRow
                  label="Gender"
                  icon={Users}
                  values={selectedSchools.map(s => s.gender)}
                />
                <ComparisonRow
                  label="Sector"
                  icon={Building2}
                  values={selectedSchools.map(s => s.type)}
                />
                <ComparisonRow
                  label="Academic Rank"
                  icon={Trophy}
                  values={selectedSchools.map(s => (s.rank ? `#${s.rank}` : 'Not ranked'))}
                  best={bestIndex(selectedSchools.map(s => (s.rank ? -s.rank : null)))}
                  highlight
                />
                <ComparisonRow
                  label="CSEC Score"
                  icon={BookOpen}
                  values={selectedSchools.map(s => (s.score != null ? s.score : 'Not ranked'))}
                  best={bestIndex(selectedSchools.map(s => (s.score != null ? s.score : null)))}
                  highlight
                />
              </tbody>
            </table>
          </div>

          {/* Disclaimer */}
          <div className="mt-6 p-4 bg-brand-50 rounded-lg text-sm text-brand-900 ring-1 ring-brand-100">
            <strong>Note:</strong> Rank and score come from the 2024 High School Performance Index,
            which measures the share of a school's Grade 11 cohort passing five or more CSEC subjects
            including Maths and English. They are not government endorsements, and a school shown as
            "Not ranked" may simply not appear in that index.
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 rounded-xl p-8 text-center">
          <Scale className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-600 mb-2">Select at least 2 schools to compare</h3>
          <p className="text-sm text-gray-500">Click "Add School" above to search and select schools for comparison.</p>
        </div>
      )}

      {/* Quick Links */}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <button
          onClick={() => setPage('schools')}
          className="p-4 bg-white rounded-xl border border-gray-200 hover:border-brand-300 hover:shadow-md transition-all text-left flex items-center gap-3"
        >
          <School className="w-8 h-8 text-brand-600" />
          <div>
            <div className="font-semibold text-gray-800">Browse All Schools</div>
            <div className="text-sm text-gray-500">Search the full school directory</div>
          </div>
        </button>
        <button
          onClick={() => setPage('rankings')}
          className="p-4 bg-white rounded-xl border border-gray-200 hover:border-amber-300 hover:shadow-md transition-all text-left flex items-center gap-3"
        >
          <Trophy className="w-8 h-8 text-amber-600" />
          <div>
            <div className="font-semibold text-gray-800">View Rankings</div>
            <div className="text-sm text-gray-500">See full academic rankings</div>
          </div>
        </button>
      </div>
    </div>
  );
};

export default CompareSchoolsPage;
